import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useFirestoreCollection } from "../../../lib/useFirestore.js";
import { bridge } from "../../../lib/bridgeCallables.js";
import { compareVersions } from "../../../lib/versionCompare.js";

/**
 * Traffic Control → Machines, v2 (slice 2.2; the approved mockup is
 * docs/ingest/mockups/machines-v2-mockup.html, approved by Ivin 2026-10-07).
 * Every computer running NYC Bridge: its hardware, what it is ALLOWED to do
 * (capabilities, pools, jobs: set by an admin, setMachineAccess), what it is
 * GOOD AT (computed by the Suite, machines.suited, with an admin override),
 * the measured medians, startup, its roots and the places an admin bound
 * them to ("Confirm this root"), the access review, and the machine items of
 * the Needs-you list. Admin-only: App.jsx routes traffic to admins, machines
 * reads are admin-gated in Firestore rules, and places, bindings and the
 * Needs-you list come from admin-only callables.
 */

const ONLINE_WINDOW_MS = 3 * 60 * 1000; // heartbeat is 60s; 3 misses = offline
const STALE_HARDWARE_MS = 2 * 60 * 60_000;

// Capability metadata (doc 2 section 1.4's meanings). `pairable: false` = the
// module is not built (or not tickable here) yet: shown greyed, kept if a
// machine already has it. `danger` marks the one that deletes.
export const CAPS = {
  verify: { label: "Verify", desc: "Read-only: hashes files under its roots and reports what it holds.", pairable: true },
  traffic: { label: "Traffic", desc: "Verify and copy orders; never deletes. With backup, can package files.", pairable: true },
  backup: { label: "Backup", desc: "Uploads, downloads and cloud read-backs through signed links; never holds a cloud key.", pairable: true },
  purge: { label: "Purge", desc: "Deletes local copies, only under the purge guard.", pairable: true, danger: true },
  capture: { label: "Capture", desc: "Triggers the rig and records captures (still on the rig bridge).", pairable: false },
  autocycle: { label: "AutoCycle", desc: "The processing queue, any job in its pools; reports its own run folders.", pairable: true },
  preview: { label: "Preview", desc: "Sends thumbnails and reference photos to the contact sheet.", pairable: false },
  render: { label: "Render", desc: "Runs Blender for job-scoped renders.", pairable: false },
  rename: { label: "Rename", desc: "Mirrors Suite name changes onto folders.", pairable: false },
};

/** Q40 (Ivin, 2026-10-07): beside AutoCycle only these; never capture, never a job list. */
export const BESIDE_AUTOCYCLE = ["verify", "traffic", "backup", "purge"];

/**
 * The capabilities after ticking or unticking `key` (pure: exported for the
 * test). Ticking AutoCycle keeps only the four allowed beside it; ticking a
 * capability not allowed beside it takes AutoCycle off.
 */
export const nextCaps = (prev, key) => {
  if (prev.includes(key)) return prev.filter((c) => c !== key);
  if (key === "autocycle") return [...prev.filter((c) => BESIDE_AUTOCYCLE.includes(c)), key];
  if (prev.includes("autocycle") && !BESIDE_AUTOCYCLE.includes(key)) return [...prev.filter((c) => c !== "autocycle"), key];
  return [...prev, key];
};

export const KINDS = ["preview", "splat", "mesh", "lidar", "artec", "render", "verify", "backup"];
const RECIPE_KIND = { "autocycle-single": "preview", "autocycle-group": "preview" };
export const MIN_RUNS = 5;
const TOOL_LABELS = { realityscan: "RealityScan", postshot: "Postshot", blender: "Blender", artecStudio: "Artec Studio", cyclone: "Cyclone", lightroom: "Lightroom", lcc: "LCC", acworker: "acworker" };
const EVIDENCE = { credential: "Credential Manager", file: "a session file", registry: "the registry", configured: "the configured check", none: "nothing found" };
export const REASONS = {
  postshot_signed_out: "Postshot signed out",
  postshot_missing: "Postshot not installed",
  postshot_sign_in_unknown: "Postshot sign-in unknown",
  disk_almost_full: "Disk almost full",
  startup_not_set: "Set up startup",
  bridge_out_of_date: "Bridge out of date",
  confirm_root: "Confirm this root",
  root_facts_changed: "Root facts changed",
};

const isObj = (v) => v !== null && typeof v === "object" && !Array.isArray(v);
const reportText = (v) => typeof v === "string" ? v : "";
const reportNumber = (v) => typeof v === "number" && Number.isFinite(v) && v >= 0;
const list = (v) => Array.isArray(v) ? v : [];
const gb = (n) => n >= 1000 ? `${(n / 1000).toFixed(1)} TB` : `${Math.round(n)} GB`;
const minutes = (ms) => `${(ms / 60000).toFixed(1)} min`;
const has = (o, k) => isObj(o) && Object.prototype.hasOwnProperty.call(o, k);

export const fmtSeen = (ms, now) => {
  if (!ms) return "never";
  const s = Math.floor((now - ms) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)} min ago`;
  if (s < 86400) return `${Math.floor(s / 3600)} hr ago`;
  return `${Math.floor(s / 86400)} days ago`;
};
const fmtDate = (ms) => ms ? new Intl.DateTimeFormat("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", timeZone: "America/New_York" }).format(ms) : "never";

/** Older bridges and incomplete records are normal; never render objects as text. */
export function hardwareLine(machine, now = Date.now()) {
  const h = machine?.hardware;
  if (!isObj(h)) return "";
  const parts = [];
  if (Array.isArray(h.gpus)) {
    if (h.gpus.length === 0) parts.push("No graphics card");
    else for (const g of h.gpus.slice(0, 8)) {
      if (reportText(g?.name)) parts.push(`${g.name}${reportNumber(g.vramGb) ? ` ${g.vramGb} GB` : ""}`);
    }
  }
  if (reportNumber(h.memoryGb)) parts.push(`${h.memoryGb} GB RAM`);
  const disks = list(h.disks).filter((d) => reportText(d?.root) && reportNumber(d?.freeGb) && reportNumber(d?.totalGb));
  for (const d of disks.slice(0, 3)) parts.push(`${d.root.replace(/[\\/]+$/, "") || "/"} ${Math.round(d.freeGb)}/${Math.round(d.totalGb)} GB free`);
  if (disks.length > 3) parts.push(`+${disks.length - 3} more`);
  if (isObj(machine.tools)) {
    const tools = Object.entries(machine.tools).filter(([, t]) => t?.present === true).slice(0, 24)
      .map(([key, t]) => `${has(TOOL_LABELS, key) ? TOOL_LABELS[key] : key}${reportText(t.version) ? ` ${t.version}` : ""}`);
    if (tools.length) parts.push(tools.join(", "));
  }
  if (parts.length && hardwareStale(machine, now)) parts.push(`as of ${fmtSeen(hardwareProbedAt(machine), now)}`);
  return parts.join(" · ");
}
// The bridge re-probes in full every hour and resends its cached report on
// every beat, so the report's age is when the last NEW full probe arrived
// (hardwareProbedAt, server time); hardwareSeenAt for records written before it.
const hardwareProbedAt = (machine) =>
  reportNumber(machine?.hardwareProbedAt) ? machine.hardwareProbedAt : reportNumber(machine?.hardwareSeenAt) ? machine.hardwareSeenAt : null;
export const hardwareStale = (machine, now) => {
  const at = hardwareProbedAt(machine);
  return at !== null && now - at > STALE_HARDWARE_MS;
};

export const machineStatus = (m, now) =>
  m.revoked ? "Revoked" : now - (m.lastSeenAt || 0) >= ONLINE_WINDOW_MS ? "Offline" : m.status === "working" ? "Working" : "Idle";

export const startupText = (m) =>
  m.startup === "logon_task" ? "Starts with Windows (sign-in task)" :
    m.startup === "console" ? "Not set up: started by hand" :
      "Unknown · this bridge does not report startup";

/** The pools a machine is in: its record's list, else the preset's default (machineAccess.ts machinePools). */
export const machinePools = (m) => Array.isArray(m.pools) ? m.pools.filter((p) => typeof p === "string") : list(m.capabilities).includes("autocycle") ? ["studio"] : [];

const scopeText = (m) => list(m.capabilities).includes("autocycle") ? "Any processing-enabled job in its pools · lease-scoped" :
  list(m.assignedJobs).length ? list(m.assignedJobs).join(", ") : "No jobs assigned";

/** What routing would use for this kind: "never", "can't", a score, or null when not rated yet. */
export const effective = (m, kind) => {
  const s = isObj(m.suited) ? m.suited[kind] : undefined;
  const o = isObj(m.suitedOverride) ? m.suitedOverride[kind] : undefined;
  if (!isObj(s)) return null;
  if (o?.score === "never") return "never";
  if (!s.can) return "can't";
  return typeof o?.score === "number" ? o.score : s.score;
};
const effectiveText = (v) => v === null ? "not rated" : typeof v === "number" ? `${v}/100` : v;

/** The kinds to show on a row: the best three it can do, then the rest. */
export const topKinds = (m, first = "") => {
  const sorted = [...KINDS].sort((a, b) => {
    const va = effective(m, a);
    const vb = effective(m, b);
    return (typeof vb === "number" ? vb : -1) - (typeof va === "number" ? va : -1);
  });
  return (first ? [first, ...sorted.filter((k) => k !== first)] : sorted).slice(0, 3);
};

/** The fleet ordered for one kind: eligible by score, then can't, never, offline, revoked. */
export const rankMachines = (machines, kind, now) => {
  const score = (m) => {
    const st = machineStatus(m, now);
    if (st === "Revoked") return -4;
    if (st === "Offline") return -3;
    const v = effective(m, kind);
    return v === "never" ? -2 : typeof v === "number" ? v : -1;
  };
  return [...machines].sort((a, b) => score(b) - score(a) || (a.label || "").localeCompare(b.label || ""));
};

/** The identifying facts of a reported root (what a binding snapshots). */
export const rootFacts = (r) => {
  const out = { path: r.path, kind: r.kind };
  if (r.serial) out.serial = r.serial;
  if (r.unc) out.unc = r.unc;
  if (r.fs) out.fs = r.fs;
  return out;
};
const sameFacts = (a, b) => {
  const x = rootFacts(a || {});
  const y = rootFacts(b || {});
  return x.path === y.path && x.kind === y.kind && x.serial === y.serial && x.unc === y.unc && x.fs === y.fs;
};

const bindingFor = (bindings, uid, rootName) =>
  bindings.find((b) => b.machineUid === uid && String(b.rootName).toLowerCase() === String(rootName).toLowerCase()) || null;

/**
 * A root's state on the tab: "bound" (an active binding whose snapshot
 * matches the report), "changed" (the facts differ: it counts for nothing
 * until confirmed again), or "unbound".
 */
export const rootState = (binding, root) => {
  if (!binding) return "unbound";
  if (binding.retiredAt === null || binding.retiredAt === undefined) {
    // Away (a disconnected share reports fewer facts): the Suite keeps the
    // binding and checks again when it is back; copies cannot count meanwhile.
    if (!root.reachable) return "unreachable";
    return sameFacts(binding.facts, root) ? "bound" : "changed";
  }
  return binding.retiredReason === "facts_changed" ? "changed" : "unbound";
};

/** "Confirm this root" is offered only where the Suite would accept it: no active binding. */
export const canConfirmRoot = (binding) => !binding || (binding.retiredAt !== null && binding.retiredAt !== undefined);

/**
 * A suggestion only (never a binding): another machine's active binding
 * whose facts name the same storage (the same share path, or for a local
 * disk the same volume serial). Returns that binding's placeId or null.
 */
export const suggestPlace = (bindings, uid, root) => {
  const match = bindings.find((b) => {
    if (b.retiredAt !== null && b.retiredAt !== undefined) return false;
    if (b.machineUid === uid && String(b.rootName).toLowerCase() === String(root.name).toLowerCase()) return false;
    if (root.unc && b.facts?.unc) return b.facts.unc.toLowerCase() === root.unc.toLowerCase();
    return !root.unc && !b.facts?.unc && !!root.serial && b.facts?.serial === root.serial;
  });
  return match ? match.placeId : null;
};

/** The access dialog's payload, or an error sentence (pure: exported for the test). */
export const accessPayload = ({ caps, pools, jobs }) => {
  const capabilities = [...new Set(caps)];
  if (!capabilities.length) return { error: "Choose at least one capability." };
  const poolList = [...new Set(String(pools || "").split(",").map((s) => s.trim()).filter(Boolean))];
  const bad = poolList.find((p) => !/^[a-z0-9][a-z0-9_-]{0,39}$/.test(p));
  if (bad) return { error: `"${bad}" is not a pool name (lowercase letters, numbers, - and _).` };
  const autocycle = capabilities.includes("autocycle");
  const jobList = autocycle ? [] : [...new Set(String(jobs || "").split(",").map((s) => s.trim()).filter(Boolean))];
  if (autocycle && capabilities.some((c) => !BESIDE_AUTOCYCLE.includes(c) && c !== "autocycle")) {
    return { error: "Beside AutoCycle only Verify, Traffic, Backup and Purge may be ticked." };
  }
  return { capabilities, pools: poolList, assignedJobs: jobList };
};

const errText = (err) => {
  const code = String(err?.code || "").replace(/^functions\//, "");
  return code === "permission-denied" ? "Only admins can do this." : (err?.message || "Something went wrong.");
};

export default function MachinesView() {
  const { data: machines, loading, error } = useFirestoreCollection("machines");
  const [release, setRelease] = useState(null); // { version, url, sha256 } | null
  const [storage, setStorage] = useState({ places: [], bindings: [], error: null });
  const [attention, setAttention] = useState({ rows: [], error: null });
  const [expanded, setExpanded] = useState(null);
  const [rankKind, setRankKind] = useState("");
  const [dialog, setDialog] = useState(null); // {type, uid?, root?, kind?}
  const [toast, setToast] = useState("");

  useEffect(() => {
    bridge.getBridgeRelease().then(setRelease).catch(() => {});
  }, []);

  const loadSide = useCallback(() => {
    bridge.listStoragePlaces()
      .then((r) => setStorage({ places: list(r?.places), bindings: list(r?.bindings), error: null }))
      .catch((err) => setStorage((s) => ({ ...s, error: errText(err) })));
    bridge.listAttention()
      .then((r) => setAttention({ rows: list(r?.rows), error: null }))
      .catch((err) => setAttention((a) => ({ ...a, error: errText(err) })));
  }, []);
  useEffect(() => {
    loadSide();
    const t = setInterval(loadSide, 60_000);
    return () => clearInterval(t);
  }, [loadSide]);

  // Re-render every 30s so "just now" and the online dots stay honest.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(t);
  }, []);
  useEffect(() => {
    if (!toast) return undefined;
    const t = setTimeout(() => setToast(""), 4500);
    return () => clearTimeout(t);
  }, [toast]);

  const fleet = useMemo(() => {
    const base = [...machines].sort((a, b) => (a.revoked ? 1 : 0) - (b.revoked ? 1 : 0) || (a.label || "").localeCompare(b.label || ""));
    return rankKind ? rankMachines(base, rankKind, now) : base;
  }, [machines, rankKind, now]);

  const errorMessage = error && (
    error.code === "permission-denied" || error.code === "firestore/permission-denied"
      ? "Access denied. Machines are visible to admins only."
      : "Machines could not be loaded. Try again in a moment."
  );

  const byUid = (uid) => machines.find((m) => m.id === uid);
  const done = (message) => {
    setDialog(null);
    setToast(message);
    loadSide();
  };
  const machineRows = attention.rows.filter((r) => r.kind === "machine");
  const counts = {
    online: machines.filter((m) => ["Idle", "Working"].includes(machineStatus(m, now))).length,
    offline: machines.filter((m) => machineStatus(m, now) === "Offline").length,
    revoked: machines.filter((m) => m.revoked).length,
  };
  const activePlaces = storage.places.filter((p) => p.retiredAt === null || p.retiredAt === undefined);

  return (
    <div className="mv2">
      <style>{CSS}</style>
      <header className="heading">
        <div>
          <h1>Machines <span className="chip">Admin only</span></h1>
          <p className="sub">Every computer running NYC Bridge. Online = heartbeat in the last 3 minutes.</p>
        </div>
        <div className="toolbar">
          <DownloadButton release={release} />
          <button type="button" className="btn btn-primary" onClick={() => setDialog({ type: "pair" })}>+ Pair a machine</button>
        </div>
      </header>

      <NeedsYouPanel rows={machineRows} error={attention.error} onReview={(row) => {
        setExpanded(row.machineUid);
        if (typeof document !== "undefined") setTimeout(() => document.getElementById(`machine-${row.machineUid}`)?.scrollIntoView({ behavior: "smooth", block: "center" }), 0);
      }} />

      {errorMessage ? (
        <div className="empty">{errorMessage}</div>
      ) : loading ? (
        <div className="empty">Loading machines…</div>
      ) : machines.length === 0 ? (
        <div className="empty">No machines paired yet. Run NYC Bridge on a computer, then click &quot;Pair a machine.&quot;</div>
      ) : (
        <>
          <div className="fleet-meta">
            <span>{machines.length} machines · {counts.online} online · {counts.offline} offline · {counts.revoked} revoked</span>
            <span>Select a machine for details</span>
          </div>
          <div className="fleet-meta">
            <label htmlFor="rank-kind">Rank by good for{" "}
              <select id="rank-kind" className="btn btn-ghost" value={rankKind} onChange={(e) => setRankKind(e.target.value)}>
                <option value="">Overview</option>
                {KINDS.map((k) => <option key={k} value={k}>{k}</option>)}
              </select>
            </label>
            <span>Suitability is separate from permission. Offline and revoked machines stay on the list.</span>
          </div>
          <table aria-label="Machines, hardware, access and suitability">
            <thead>
              <tr><th>Machine / hardware</th><th>Status / startup</th><th>Allowed</th><th>Good for / measured</th><th>Bridge / access review</th><th><span className="muted">Actions</span></th></tr>
            </thead>
            {fleet.map((m) => (
              <MachineRow key={m.id} m={m} now={now} rankKind={rankKind} open={expanded === m.id}
                bindings={storage.bindings} places={storage.places}
                onToggle={() => setExpanded(expanded === m.id ? null : m.id)}
                onAction={(type, extra = {}) => setDialog({ type, uid: m.id, ...extra })} />
            ))}
          </table>
        </>
      )}

      <section className="places" aria-labelledby="places-title">
        <div className="section-head"><h2 id="places-title">Places</h2><span className="small muted">A shared NAS is one place, even through different drive letters.</span></div>
        {storage.error ? <p className="small muted">Places could not be loaded: {storage.error}</p> : (
          <div className="places-list">
            {activePlaces.length === 0 && <p className="small muted">No places yet. Confirm a machine&apos;s root to make the first one.</p>}
            {[...activePlaces].sort((a, b) => (a.kind === "nas" ? 0 : 1) - (b.kind === "nas" ? 0 : 1) || a.label.localeCompare(b.label)).map((p) => {
              const bound = storage.bindings.filter((b) => b.placeId === p.id && (b.retiredAt === null || b.retiredAt === undefined))
                .map((b) => `${byUid(b.machineUid)?.label || b.machineUid} / ${b.rootName}`);
              return (
                <div className="place" key={p.id}>
                  <b>{p.label}</b> <span className="chip">{p.kind}</span>
                  <div className="mono small muted">{p.id}</div>
                  <p>{bound.join(" · ") || "No active bindings"}</p>
                </div>
              );
            })}
          </div>
        )}
      </section>
      <p className="footer">Access review · TPN TS-1.7 · The full fleet, including offline and revoked machines, with last use and last seen for the quarterly review. Machine access and root confirmations are admin-only and audited.</p>

      {dialog?.type === "pair" && <AccessDialog onClose={() => setDialog(null)} onDone={done} />}
      {dialog?.type === "access" && byUid(dialog.uid) && <AccessDialog machine={byUid(dialog.uid)} onClose={() => setDialog(null)} onDone={done} />}
      {dialog?.type === "root" && byUid(dialog.uid) && (
        <RootDialog machine={byUid(dialog.uid)} rootName={dialog.root} places={activePlaces} bindings={storage.bindings}
          machines={machines} onClose={() => setDialog(null)} onDone={done} />
      )}
      {dialog?.type === "override" && byUid(dialog.uid) && <OverrideDialog machine={byUid(dialog.uid)} kind={dialog.kind} onClose={() => setDialog(null)} onDone={done} />}
      {dialog?.type === "revoke" && byUid(dialog.uid) && <RevokeDialog machine={byUid(dialog.uid)} onClose={() => setDialog(null)} onDone={done} />}
      {toast && <div className="toast" role="status">{toast}</div>}
    </div>
  );
}

// ── Needs you (machine items; the same list as Home) ─────────────────────────
export function NeedsYouPanel({ rows, error, onReview }) {
  return (
    <section className="attention" aria-labelledby="needs-title">
      <div className="section-head">
        <h2 id="needs-title">Needs you <span className="chip">{rows.length}</span></h2>
        <span className="small muted">The same attention list shown on Home · machine items</span>
      </div>
      {error ? <p className="small muted">The list could not be loaded: {error}</p> : rows.length === 0 ? <p className="small muted">Nothing needs you.</p> : (
        <div className="attention-list">
          {rows.map((r) => (
            <div className="attention-item" key={r.id}>
              <span className="dot work" />
              <div>
                <b>{REASONS[r.reasonCode] || r.reasonCode} · {r.machineLabel || r.machineUid}</b>
                <div className="small muted">{r.detail}</div>
              </div>
              <button type="button" className="link small" onClick={() => onReview(r)}>Review →</button>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

// ── One machine ──────────────────────────────────────────────────────────────
function Chip({ cap }) {
  return <span className={`chip cap-${cap}`}>{cap}</span>;
}

function hardwareSummary(m, now) {
  const h = m.hardware;
  if (!isObj(h)) return <span className="muted">No hardware report: update the bridge or wait for its first report</span>;
  const cards = list(h.gpus).map((g) => `${String(g?.name || "").replace("NVIDIA GeForce ", "").replace("NVIDIA ", "")} · ${reportNumber(g?.vramGb) ? `${g.vramGb} GB` : "VRAM not reported"}`).join(" + ") || "No graphics card";
  const disks = list(h.disks).filter((d) => reportText(d?.root) && reportNumber(d?.freeGb)).map((d) => `${d.root.replace(/\\$/, "")} ${gb(d.freeGb)} free`).join(" · ");
  return (
    <>
      {cards}<br />{reportNumber(h.memoryGb) ? `${h.memoryGb} GB RAM` : "RAM not reported"}{disks ? ` · ${disks}` : ""}
      {hardwareStale(m, now) && <div className="muted">As of {fmtSeen(hardwareProbedAt(m), now)} · full report is stale</div>}
    </>
  );
}

function MachineRow({ m, now, rankKind, open, bindings, places, onToggle, onAction }) {
  const state = machineStatus(m, now);
  const working = list(m.working).filter((w) => typeof w === "string");
  const kinds = topKinds(m, rankKind);
  const stats = isObj(m.runStats) ? m.runStats : {};
  const recipe = Object.keys(stats).find((k) => !rankKind || RECIPE_KIND[k] === rankKind);
  const measured = recipe ? stats[recipe] : null;
  const firstWhy = isObj(m.suited) && isObj(m.suited[kinds[0]]) ? list(m.suited[kinds[0]].why)[0] : "Not rated yet: the Suite rates a machine on its next heartbeat";
  const stale = m.latestVersion && compareVersions(m.version, m.latestVersion) !== 0;
  return (
    <tbody id={`machine-${m.id}`}>
      <tr className={`machine-row ${open ? "expanded" : ""} ${m.revoked ? "revoked" : ""}`}>
        <td data-label="Machine">
          <button type="button" className="machine-name" aria-expanded={open} aria-controls={`detail-${m.id}`} onClick={onToggle}>
            <span className="chevron">{open ? "⌄" : "›"}</span>{m.label}
          </button>
          <div className="host mono">{m.hostname} · {m.platform}</div>
          <div className="hardware-summary" title={hardwareLine(m, now)}>{hardwareSummary(m, now)}</div>
        </td>
        <td data-label="Status / startup">
          <span className={`dot ${state === "Idle" ? "on" : state === "Working" ? "work" : ""}`} /><strong>{state}</strong>
          {working.length > 0 && <div className="small muted">{state === "Working" ? "" : "Last reported: "}{working.join(", ")}</div>}
          <div className="startup-short">{startupText(m)}</div>
        </td>
        <td data-label="Allowed">
          {list(m.capabilities).map((c) => <Chip key={c} cap={c} />)}
          {m.revoked && <p className="small muted">Previous access · none effective</p>}
          <p className="scope">Pools: {machinePools(m).join(", ") || "none"}</p>
          <p className="scope">Jobs: {scopeText(m)}</p>
          {!m.revoked && <button type="button" className="link small" onClick={() => onAction("access")}>Edit access</button>}
        </td>
        <td data-label="Good for / measured">
          {kinds.map((k) => <div className="good-line" key={k}><span>{k}</span><span className="score">{effectiveText(effective(m, k))}</span></div>)}
          <p className="rank-note">{firstWhy}</p>
          {measured ? (
            <p className="row-note">{recipe}<br />Median {minutes(measured.medianMs || 0)} · {measured.n || 0} runs{(measured.n || 0) < MIN_RUNS && <><br />Ranking by hardware until {MIN_RUNS} runs</>}</p>
          ) : <p className="row-note">No measured runs{rankKind ? ` for ${rankKind}` : ""}</p>}
          {m.revoked && <p className="row-note">Historical scores · no routing</p>}
        </td>
        <td data-label="Bridge / access review">
          <div className={`mono ${stale ? "ver-stale" : "muted"}`}>
            {m.version}
            {/* any mismatch, not just older: a pending ROLLBACK also shows ↻ */}
            {stale && <span className="arrow" title={`Will change to ${m.latestVersion} when idle`} aria-label={`Change to ${m.latestVersion} pending`}> ↻</span>}
          </div>
          <div className="small muted">Seen {fmtSeen(m.lastSeenAt, now)}</div>
          <div className="row-note">Last use<br />{fmtDate(m.lastWorkingAt)}</div>
        </td>
        <td>
          {!m.revoked ? <button type="button" className="revoke" onClick={() => onAction("revoke")}>Revoke</button> : <span className="small muted">Access ended</span>}
        </td>
      </tr>
      <tr id={`detail-${m.id}`} hidden={!open}>
        <td className="detail-cell" colSpan={6}>
          {open && <MachineDetail m={m} now={now} bindings={bindings} places={places} onToggle={onToggle} onAction={onAction} />}
        </td>
      </tr>
    </tbody>
  );
}

export function MachineDetail({ m, now, bindings, places, onToggle, onAction }) {
  const h = isObj(m.hardware) ? m.hardware : null;
  const stats = isObj(m.runStats) ? Object.entries(m.runStats) : [];
  return (
    <section className="detail" aria-label={`${m.label} details`}>
      <div className="section-head"><h2>{m.label} <span className="muted">/ machine details</span></h2><button type="button" className="link small" onClick={onToggle}>Close details</button></div>
      <div className="detail-grid">
        <section className="panel">
          <h3>Hardware &amp; tools</h3>
          {!h ? (
            <><p>No hardware report yet.</p><p className="muted small">CPU, RAM, GPUs, disks, OS, tools and report history are unknown. Missing reports are not evidence of missing tools.</p></>
          ) : (
            <>
              <dl className="facts">
                <dt>CPU</dt><dd>{reportText(h.cpu?.model) || "Unknown"}{reportNumber(h.cpu?.cores) ? ` · ${h.cpu.cores} cores` : ""}{reportNumber(h.cpu?.threads) ? ` / ${h.cpu.threads} threads` : ""}</dd>
                <dt>Memory</dt><dd>{reportNumber(h.memoryGb) ? `${h.memoryGb} GB RAM` : "not reported"}</dd>
                {list(h.gpus).map((g, i) => <React.Fragment key={i}><dt>GPU {i + 1}</dt><dd>{reportText(g?.name)} · {reportNumber(g?.vramGb) ? `${g.vramGb} GB VRAM` : "VRAM not reported"}<br />Driver {reportText(g?.driver) || "not reported"} · source {reportText(g?.source)}</dd></React.Fragment>)}
                <dt>OS</dt><dd>{reportText(h.os)}</dd>
                <dt>Last full report</dt><dd>{fmtDate(hardwareProbedAt(m))}{hardwareStale(m, now) ? ` · as of ${fmtSeen(hardwareProbedAt(m), now)}` : ""}</dd>
                <dt>Free space check</dt><dd>{fmtDate(h.freeCheckedAt)}</dd>
              </dl>
              {list(h.disks).filter((d) => reportNumber(d?.totalGb) && d.totalGb > 0 && reportNumber(d?.freeGb)).map((d) => {
                const pct = Math.round(d.freeGb / d.totalGb * 100);
                return (
                  <div className="disk" key={d.root}>
                    <div className="disk-label"><strong className="mono">{d.root}</strong><span>{gb(d.freeGb)} / {gb(d.totalGb)} free</span></div>
                    <div className={`bar ${pct < 10 ? "low" : ""}`} role="meter" aria-label={`${d.root} free space`} aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}><span style={{ width: `${pct}%` }} /></div>
                    <div className="small muted">{reportText(d.fs) || "filesystem not reported"} · {d.kind} · {pct}% free</div>
                  </div>
                );
              })}
            </>
          )}
          {isObj(m.tools) && (
            <>
              <h3 style={{ marginTop: 16 }}>Tools</h3>
              <div className="tools">
                {Object.entries(m.tools).map(([k, t]) => (
                  <div className="tool" key={k}>
                    <span>{has(TOOL_LABELS, k) ? TOOL_LABELS[k] : k}</span>
                    <span className={t?.present ? "" : "muted"}>{t?.present ? `Present${reportText(t.version) ? ` · ${t.version}` : ""}${k === "postshot" ? postshotText(t) : ""}` : "Missing"}</span>
                  </div>
                ))}
              </div>
            </>
          )}
          {list(m.hardwareHistory).length > 0 && (
            <details>
              <summary>Hardware history · last {list(m.hardwareHistory).length} reports</summary>
              <ul className="history">
                {[...list(m.hardwareHistory)].reverse().map((e, i) => (
                  <li key={i}>{fmtDate(e?.receivedAt)} · {list(e?.hardware?.disks).map((d) => `${d.root} ${gb(d.freeGb || 0)} free`).join(" · ")}</li>
                ))}
              </ul>
            </details>
          )}
        </section>

        <section className="panel">
          <h3>Allowed</h3>
          {list(m.capabilities).map((c) => <Chip key={c} cap={c} />)}
          <p className="small">Pools: <span className="mono">{machinePools(m).join(", ") || "none"}</span></p>
          <p className="small">Assigned jobs: <span className="mono">{list(m.assignedJobs).join(", ") || "none"}</span></p>
          <p className="small muted">{scopeText(m)}</p>
          {m.revoked ? <div className="callout">Revoked: no effective access. Pair again to bring this machine back.</div> : <button type="button" className="btn btn-ghost" onClick={() => onAction("access")}>Edit access</button>}
          <h3 style={{ marginTop: 20 }}>Good for <span className="muted">/ why</span></h3>
          <p className="small muted">Computed by the Suite. Scores rank eligible machines; routing also checks access, pools and job scope. Scores never grant permission.</p>
          {KINDS.map((k) => {
            const s = isObj(m.suited) ? m.suited[k] : null;
            const o = isObj(m.suitedOverride) ? m.suitedOverride[k] : null;
            return (
              <div className="suit" key={k}>
                <div className="suit-head">
                  <strong>{k} <span className="score">{effectiveText(effective(m, k))}</span></strong>
                  {!m.revoked && <button type="button" className="link small" onClick={() => onAction("override", { kind: k })}>{o ? "Edit override" : "Set override"}</button>}
                </div>
                <ul className="why">{(isObj(s) ? list(s.why) : ["Not rated yet"]).map((w, i) => <li key={i}>{w}</li>)}</ul>
                {isObj(o) && (
                  <div className="override">
                    Admin override: <b>{o.score === "never" ? "never" : `${o.score}/100`}</b> · computed {isObj(s) ? (s.can ? `${s.score}/100` : "can't") : "not rated"}<br />
                    {fmtDate(o.at)}<br />{o.note}<br />Does not change capabilities.
                  </div>
                )}
              </div>
            );
          })}
        </section>

        <section className="panel full">
          <h3>Measured <span className="muted">/ per recipe</span></h3>
          {stats.length ? (
            <div className="metrics">
              {stats.map(([name, s]) => (
                <div className="metric" key={name}>
                  <strong className="mono">{name}</strong>
                  median {minutes(s?.medianMs || 0)}, 9 in 10 within {minutes(s?.p90Ms || 0)}, last {minutes(s?.lastMs || 0)}, {s?.failures || 0} {s?.failures === 1 ? "failure" : "failures"}, {s?.n || 0} runs
                  <div className="muted small">Last run: {fmtDate(s?.lastAt)}</div>
                  {(s?.n || 0) < MIN_RUNS && <div className="callout warn">Ranking by hardware until {MIN_RUNS} runs. This median is still provisional.</div>}
                </div>
              ))}
            </div>
          ) : <p className="small muted">No measured runs yet. No median, 9-in-10 time or failure count is available.</p>}
        </section>

        <section className="panel full">
          <div className="section-head"><h3>Roots and places</h3><span className="small muted">Reported facts are not bindings. Each root needs its own confirmation.</span></div>
          {list(m.roots).length === 0 ? <p className="muted small">No roots reported. Configure roots in the bridge&apos;s settings, or update the bridge.</p> : (
            <div className="root-grid">
              {list(m.roots).map((r) => {
                const b = bindingFor(bindings, m.id, r.name);
                const st = rootState(b, r);
                const place = b ? places.find((p) => p.id === b.placeId) : null;
                return (
                  <article className="root" id={`root-${m.id}-${r.name}`} key={r.name}>
                    <h3>{r.name} <span className="muted mono">{r.path}</span></h3>
                    <FactsList root={r} />
                    <div className="binding">
                      {st === "bound" && <><b>Bound to place: {place?.label || b.placeId}</b> (<span className="mono">{b.placeId}</span>), confirmed by an admin on {fmtDate(b.approvedAt)}.</>}
                      {st === "changed" && <><b>Facts changed since confirmed</b>{b?.facts?.serial !== r.serial ? <> (serial was <span className="mono">{b?.facts?.serial || "none"}</span>, now <span className="mono">{r.serial || "none"}</span>)</> : null}: binding retired, copies unverified until confirmed again.<br />Previous place: {place?.label || b?.placeId}.</>}
                      {st === "unbound" && <b>Not confirmed: copies here count for nothing.</b>}
                      {st === "unreachable" && <><b>Bound to place: {place?.label || b.placeId}</b>, not reachable at the last report: copies here cannot be checked until it is back. The binding stays; its facts are checked again when it returns.</>}
                    </div>
                    {canConfirmRoot(b) && !m.revoked && <button type="button" className="btn btn-ghost" onClick={() => onAction("root", { root: r.name })}>Confirm this root</button>}
                  </article>
                );
              })}
            </div>
          )}
        </section>

        <section className="panel">
          <h3>Startup</h3>
          <p>{startupText(m)}</p>
          <p className="small muted">{m.startup === "logon_task" ? "Task Scheduler at Windows sign-in, with a signed-in desktop for graphics tools. Restarts on failure; no console window." : m.startup === "console" ? "Install the sign-in task on this computer:" : "This bridge version does not report startup; whether the task is installed is not known."}</p>
          {m.startup === "console" && <div className="callout mono">nyc-bridge --install-startup</div>}
          <details><summary>Bridge log · last 20 lines <i>planned</i></summary><p className="small muted">Not sent yet: the bridge keeps its log on the computer (%APPDATA%\nyc-bridge\logs, nyc-bridge --status).</p></details>
        </section>

        <section className="panel">
          <h3>Access review <span className="muted">/ TPN TS-1.7</span></h3>
          <dl className="facts">
            <dt>Machine</dt><dd>{m.label} · <span className="mono">{m.hostname}</span></dd>
            <dt>Paired</dt><dd>{fmtDate(m.pairedAt)}</dd>
            <dt>Last use</dt><dd>{fmtDate(m.lastWorkingAt)}</dd>
            <dt>Last seen</dt><dd>{fmtDate(m.lastSeenAt)} · {fmtSeen(m.lastSeenAt, now)}</dd>
            <dt>Access changed</dt><dd>{m.accessChangedAt ? fmtDate(m.accessChangedAt) : "not since pairing"}</dd>
            <dt>Bridge</dt><dd>{m.version}{m.latestVersion && compareVersions(m.version, m.latestVersion) !== 0 ? ` · ↻ ${m.latestVersion} when idle and online` : " · up to date"}</dd>
            <dt>Access</dt><dd>{m.revoked ? `Revoked on ${fmtDate(m.revokedAt)}` : "Paired · machine record checked on every call"}</dd>
          </dl>
          <p className="small muted" style={{ marginTop: 12 }}>Changes to capabilities, jobs and pools are audited. The machine sees the change on its next call; no re-pairing.</p>
        </section>
      </div>
    </section>
  );
}

export const postshotText = (t) => t?.signedIn === true ? ` · signed in (${EVIDENCE[t.signInEvidence] || "reported"})` :
  t?.signedIn === false ? " · not signed in" : " · sign-in not reported (update the bridge)";

function FactsList({ root }) {
  return (
    <dl className="facts">
      <dt>Path</dt><dd className="mono">{root.path}</dd>
      <dt>Kind</dt><dd>{root.kind}</dd>
      <dt>Volume serial</dt><dd className="mono">{root.serial || "not reported"}</dd>
      <dt>Resolves to UNC</dt><dd className="mono">{root.unc || "None · local volume"}</dd>
      <dt>Filesystem</dt><dd>{root.fs || "not reported"}</dd>
      <dt>Free / total</dt><dd>{reportNumber(root.freeGb) ? gb(root.freeGb) : "?"} / {reportNumber(root.totalGb) ? gb(root.totalGb) : "?"}</dd>
      <dt>Reachable</dt><dd>{root.reachable ? "Yes" : "No"} · at last report</dd>
    </dl>
  );
}

// ── Download button ──────────────────────────────────────────────────────────
export function DownloadButton({ release }) {
  if (release) {
    return (
      <a href={release.url} target="_blank" rel="noopener noreferrer" className="btn btn-ghost" style={{ textDecoration: "none" }}>
        ↓ Download Bridge for Windows · v{release.version}
      </a>
    );
  }
  return (
    <button type="button" className="btn btn-ghost" title="Available with the first packaged bridge release" disabled>
      ↓ Download Bridge for Windows
    </button>
  );
}

// ── Dialogs ──────────────────────────────────────────────────────────────────
function Dialog({ title, subtitle, onClose, busy, children }) {
  return (
    <div className="overlay" onClick={busy ? undefined : onClose}>
      <div className="dialog" role="dialog" aria-modal="true" aria-labelledby="dialog-title" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="close" aria-label="Close dialog" onClick={onClose} disabled={busy}>×</button>
        <h2 id="dialog-title">{title}</h2>
        {subtitle && <p className="sub">{subtitle}</p>}
        {children}
      </div>
    </div>
  );
}

function DialogButtons({ label, busy, danger, error, onCancel, disabled, extra }) {
  return (
    <>
      {error && <p className="form-error" role="alert">{error}</p>}
      <div className="dialog-actions">
        {extra}
        <button type="button" className="btn btn-ghost" onClick={onCancel} disabled={busy}>Cancel</button>
        <button type="submit" className={`btn ${danger ? "btn-danger" : "btn-primary"}`} disabled={busy || disabled}>{busy ? "Saving…" : label}</button>
      </div>
    </>
  );
}

/** Pair a machine (no `machine`) or edit an existing one's access. */
export function AccessDialog({ machine = null, onClose, onDone }) {
  const [code, setCode] = useState("");
  const [label, setLabel] = useState("");
  const [caps, setCaps] = useState(machine ? list(machine.capabilities) : ["verify"]);
  const [pools, setPools] = useState(machine ? machinePools(machine).join(", ") : "");
  const [jobs, setJobs] = useState(machine ? list(machine.assignedJobs).join(", ") : "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [pairedAs, setPairedAs] = useState("");
  const autocycle = caps.includes("autocycle");
  const canPair = !machine ? code.replace(/[^0-9a-zA-Z]/g, "").length === 6 && label.trim().length > 0 && caps.length > 0 : caps.length > 0;

  const submit = async (e) => {
    e.preventDefault();
    const p = accessPayload({ caps, pools, jobs });
    if (p.error) return setError(p.error);
    setBusy(true);
    setError(null);
    try {
      if (machine) {
        const res = await bridge.setMachineAccess({ uid: machine.id, capabilities: p.capabilities, assignedJobs: p.assignedJobs, pools: p.pools });
        onDone(`Access saved for ${machine.label}. Audited; applies on its next call.${res?.claimsSynced === false ? " Its sign-in token could not be updated (its own processing switch reads it): save again." : ""}`);
      } else {
        const res = await bridge.claimPairingCode({ code: code.trim(), label: label.trim(), capabilities: p.capabilities, assignedJobs: p.assignedJobs, ...(pools.trim() ? { pools: p.pools } : {}) });
        setPairedAs(res.label);
      }
    } catch (err) {
      const c = String(err?.code || "").replace(/^functions\//, "");
      setError(c === "not-found" && !machine ? "Unknown or expired code — check the machine's screen for a fresh one."
        : c === "already-exists" ? "That code was already claimed." : `${machine ? "Saving" : "Pairing"} failed: ${errText(err)}`);
    } finally {
      setBusy(false);
    }
  };

  if (pairedAs) {
    return (
      <Dialog title="Pair a machine" onClose={() => onDone(`Paired as ${pairedAs}.`)}>
        <div style={{ textAlign: "center", padding: "18px 0" }}>
          <div style={{ fontSize: 30, marginBottom: 10 }}>✓</div>
          <div style={{ fontSize: 15, fontWeight: 500, marginBottom: 4 }}>Paired as &quot;{pairedAs}&quot;</div>
          <div className="small muted" style={{ marginBottom: 18 }}>The machine&apos;s screen flips to &quot;Paired&quot; within a few seconds.</div>
          <button type="button" className="btn btn-primary" onClick={() => onDone(`Paired as ${pairedAs}.`)}>Done</button>
        </div>
      </Dialog>
    );
  }
  return (
    <Dialog title={machine ? `Edit access · ${machine.label}` : "Pair a machine"} subtitle={machine ? "Update capabilities, jobs and pools without re-pairing." : "Run NYC Bridge on the new computer — it shows a code."} onClose={onClose} busy={busy}>
      <form onSubmit={submit}>
        {!machine && (
          <>
            <div className="field">
              <label className="lbl" htmlFor="pair-code">Code on the machine&apos;s screen</label>
              <input id="pair-code" className="code-input" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="XX-XXXX" maxLength={8} autoComplete="off" autoFocus />
            </div>
            <div className="field">
              <label className="lbl" htmlFor="pair-name">Name this machine</label>
              <input id="pair-name" className="txt" value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. Capture Laptop 2" maxLength={60} />
            </div>
          </>
        )}
        <div className="field">
          <span className="lbl">What it&apos;s allowed to do</span>
          <div className="caps">
            {Object.entries(CAPS).map(([key, meta]) => {
              const selected = caps.includes(key);
              return (
                <label key={key} className={`cap-opt ${selected ? "sel" : ""} ${meta.danger ? "danger" : ""} ${meta.pairable ? "" : "dis"}`}>
                  <input type="checkbox" name="capability" value={key} checked={selected} disabled={!meta.pairable || busy} onChange={() => setCaps((prev) => nextCaps(prev, key))} />
                  <span><span className="t">{meta.label}</span><span className="d">{meta.desc}{meta.pairable ? "" : " Not built yet."}</span></span>
                </label>
              );
            })}
          </div>
          {autocycle && (
            <div className="callout">
              Processing computer: AutoCycle has no assigned jobs. It takes captures only from the jobs switched on for processing, in its pools, and none while processing is paused. Beside it only Verify, Traffic, Backup and Purge, each its own tick; never Capture.
            </div>
          )}
          {caps.includes("purge") && (
            <div className="callout purge-warning">Purge deletes only under the guard: two other verified copies with the same checksum in different places, one in the cloud. Grace period met, no hold, never the last copy. The guard is checked again immediately before deletion.</div>
          )}
        </div>
        <div className="field">
          <label className="lbl" htmlFor="pools">Pools · comma separated</label>
          <input id="pools" className="txt" value={pools} onChange={(e) => setPools(e.target.value)} placeholder={autocycle ? "studio (the default)" : "studio, event-2026-11"} />
          <p className="hint">Taking a processing computer out of &quot;studio&quot; stops it taking processing work on its next call.</p>
        </div>
        <div className="field">
          <label className="lbl" htmlFor="jobs">Assigned jobs · comma separated</label>
          <input id="jobs" className="txt" value={autocycle ? "" : jobs} onChange={(e) => setJobs(e.target.value)} placeholder="xrb_260930" disabled={autocycle} />
          <p className="hint">AutoCycle uses a lease for each job. Other modules with no assigned jobs cannot touch job-scoped work.</p>
        </div>
        <div className="callout">{machine ? "setMachineAccess · admin-only and audited. The machine sees the change on its next call." : "Pairing is admin-only and audited. Codes expire after 5 minutes; the bridge issues a fresh code automatically."}</div>
        <DialogButtons label={machine ? "Save access" : "Pair"} busy={busy} error={error} onCancel={onClose} disabled={!canPair} />
      </form>
    </Dialog>
  );
}

export function RootDialog({ machine, rootName, places, bindings, machines, onClose, onDone }) {
  const live = list(machine.roots).find((r) => r.name === rootName);
  // The snapshot the admin is looking at is frozen when the dialog opens:
  // that, never a newer report, is what is sent. If the machine reports
  // different facts meanwhile, confirming is off until the dialog is reopened.
  const [root] = useState(() => live);
  const changed = !live || !root || !sameFacts(rootFacts(live), rootFacts(root));
  const [placeId, setPlaceId] = useState("");
  const [newLabel, setNewLabel] = useState("");
  const [newKind, setNewKind] = useState(root?.unc ? "nas" : "local");
  const [checked, setChecked] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  if (!root) return null;
  const suggested = suggestPlace(bindings, machine.id, root);
  const suggestedBinding = suggested ? bindings.find((b) => b.placeId === suggested && (b.retiredAt === null || b.retiredAt === undefined)) : null;
  const suggestedPlace = places.find((p) => p.id === suggested);
  const choices = places.filter((p) => p.kind === "local" || p.kind === "nas");

  const submit = async (e) => {
    e.preventDefault();
    if (changed) return setError("The machine reported different facts while this was open. Close and look again.");
    if (!placeId || !checked) return setError("Choose a place and confirm the facts.");
    if (placeId === "new" && !newLabel.trim()) return setError("Name the new place.");
    setBusy(true);
    setError(null);
    try {
      const place = placeId === "new" ? { newPlace: { kind: newKind, label: newLabel.trim() } } : { placeId };
      const res = await bridge.confirmStoragePlace({ uid: machine.id, rootName: root.name, facts: rootFacts(root), place });
      onDone(`${machine.label} / ${root.name} bound to ${placeId === "new" ? newLabel.trim() : (places.find((p) => p.id === placeId)?.label || res.placeId)}. Audited; copies still need verification.`);
    } catch (err) {
      setError(errText(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog title={`Confirm this root · ${machine.label} / ${root.name}`} subtitle="The bridge reports facts. Only an admin can bind this root to a place." onClose={onClose} busy={busy}>
      <form onSubmit={submit}>
        {changed && <div className="callout purge-warning" role="alert">The machine reported different facts for this root while this was open. Close this and look again; nothing can be confirmed against facts you have not seen.</div>}
        <h3>Facts snapshot to confirm</h3>
        <FactsList root={root} />
        <p className="hint">Reported by {machine.hostname} · {fmtDate(machine.hardwareProbedAt)}. This exact snapshot is kept with the confirmation; if the report changes first, the confirmation is refused.</p>
        {suggestedPlace && (
          <div className="callout warn">
            These facts match the {suggestedPlace.label} that {machines.find((x) => x.id === suggestedBinding?.machineUid)?.label || "another machine"}&apos;s {suggestedBinding?.rootName} root is bound to. <b>Suggestion only: nothing is bound until you confirm.</b>
          </div>
        )}
        <div className="field" style={{ marginTop: 16 }}>
          <label className="lbl" htmlFor="place-select">Existing place or a new one</label>
          <select id="place-select" className="txt" value={placeId} onChange={(e) => setPlaceId(e.target.value)} required>
            <option value="">Choose a place…</option>
            {choices.map((p) => <option key={p.id} value={p.id}>{p.label} · {p.kind}{p.id === suggested ? " (suggested)" : ""}</option>)}
            <option value="new">Create a new place…</option>
          </select>
        </div>
        {placeId === "new" && (
          <>
            <div className="field"><label className="lbl" htmlFor="place-name">New place label</label><input id="place-name" className="txt" maxLength={60} value={newLabel} onChange={(e) => setNewLabel(e.target.value)} placeholder="e.g. P6 work disk" /></div>
            <div className="field"><label className="lbl" htmlFor="place-kind">Place kind</label>
              <select id="place-kind" className="txt" value={newKind} onChange={(e) => setNewKind(e.target.value)}><option value="local">Local</option><option value="nas">NAS</option></select>
            </div>
          </>
        )}
        <div className="callout">Confirm each machine&apos;s root separately. Two roots bound to the same NAS count as one place, never as two copies in different places. Cloud places cannot be assigned to machine roots.</div>
        <label className="ack"><input type="checkbox" checked={checked} onChange={(e) => setChecked(e.target.checked)} /><span>I checked these facts and the physical storage. Bind only this machine&apos;s root to the selected place.</span></label>
        <DialogButtons label="Confirm root" busy={busy} error={error} onCancel={onClose} disabled={changed} />
      </form>
    </Dialog>
  );
}

export function OverrideDialog({ machine, kind, onClose, onDone }) {
  const s = isObj(machine.suited) ? machine.suited[kind] : null;
  const o = isObj(machine.suitedOverride) ? machine.suitedOverride[kind] : null;
  const [mode, setMode] = useState(o?.score === "never" ? "never" : "score");
  const [score, setScore] = useState(typeof o?.score === "number" ? String(o.score) : String(isObj(s) ? s.score : 50));
  const [note, setNote] = useState(o?.note || "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const save = async (override) => {
    setBusy(true);
    setError(null);
    try {
      await bridge.setSuitabilityOverride({ uid: machine.id, kind, override });
      onDone(override ? `${kind} override saved. Capabilities unchanged.` : "Override cleared. Computed ranking restored; capabilities unchanged.");
    } catch (err) {
      setError(errText(err));
    } finally {
      setBusy(false);
    }
  };
  const submit = (e) => {
    e.preventDefault();
    const n = Number(score);
    if (!note.trim() || (mode === "score" && (!Number.isInteger(n) || n < 0 || n > 100))) return setError("Add a reason and a score from 0 to 100, or choose never.");
    save({ score: mode === "never" ? "never" : n, note: note.trim() });
  };
  return (
    <Dialog title={`Suitability override · ${machine.label} / ${kind}`} subtitle="An admin override changes ranking. It does not change capabilities." onClose={onClose} busy={busy}>
      <form onSubmit={submit}>
        <div className="callout">Computed: <b>{isObj(s) ? (s.can ? `${s.score}/100` : "can't") : "not rated"}</b><ul className="why">{(isObj(s) ? list(s.why) : []).map((w, i) => <li key={i}>{w}</li>)}</ul></div>
        <div className="field" style={{ marginTop: 16 }}>
          <label className="lbl" htmlFor="override-mode">Override</label>
          <select id="override-mode" className="txt" value={mode} onChange={(e) => setMode(e.target.value)}><option value="score">Set a score</option><option value="never">Never route this kind here</option></select>
        </div>
        {mode === "score" && <div className="field"><label className="lbl" htmlFor="override-score">Score · 0 to 100</label><input id="override-score" className="txt" type="number" min={0} max={100} step={1} value={score} onChange={(e) => setScore(e.target.value)} /></div>}
        <div className="field"><label className="lbl" htmlFor="override-note">Why override?</label><textarea id="override-note" className="txt" rows={3} maxLength={240} value={note} onChange={(e) => setNote(e.target.value)} /></div>
        <p className="hint">Audited. Missing hard needs still block work, even with a score override.</p>
        <DialogButtons label="Save override" busy={busy} error={error} onCancel={onClose}
          extra={o ? <button type="button" className="btn btn-ghost" disabled={busy} onClick={() => save(null)}>Clear override</button> : null} />
      </form>
    </Dialog>
  );
}

export function RevokeDialog({ machine, onClose, onDone }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await bridge.revokeMachine({ uid: machine.id });
      onDone(`${machine.label} revoked. It is refused on its next call.`);
    } catch (err) {
      setError(`Revoke failed: ${errText(err)}`);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog title={`Revoke ${machine.label}?`} subtitle="It is refused on its next call; to bring it back, pair it again." onClose={onClose} busy={busy}>
      <form onSubmit={submit}>
        <div className="callout purge-warning">{machine.hostname} loses every capability, pool and job permission, and its roots stop counting as places.{list(machine.working).length ? ` Current activity: ${list(machine.working).join(", ")}. Revocation takes effect at its next call.` : ""}</div>
        <p className="hint">The record stays in the quarterly access review.</p>
        <DialogButtons label={`Revoke ${machine.label}`} danger busy={busy} error={error} onCancel={onClose} />
      </form>
    </Dialog>
  );
}

// The approved mockup's styles, scoped to this view (.mv2).
const CSS = `
.mv2 { --bg:#F5F3F0; --surface:#FFFFFF; --surface-alt:#EDEBE8; --border:#E0DDD8; --text:#1A1A1A; --muted:#6B6B6B; --green:#10B981; --amber:#F59E0B; --danger:#EF4444;
  color:var(--text); font-family:'DM Sans',system-ui,sans-serif; padding:28px 32px; font-size:13px; font-weight:300; line-height:1.5; max-width:1280px; }
.mv2 *, .mv2 *::before, .mv2 *::after { box-sizing:border-box; }
.mv2 [hidden] { display:none !important; }
.mv2 h1, .mv2 h2, .mv2 h3, .mv2 p, .mv2 dl, .mv2 dd, .mv2 ul { margin:0; }
.mv2 .mono, .mv2 code, .mv2 pre { font-family:'DM Mono',monospace; }
.mv2 h1 { font-size:20px; font-weight:500; margin-bottom:4px; } .mv2 h2 { font-size:16px; font-weight:500; } .mv2 h3 { font-size:13px; font-weight:500; margin-bottom:8px; }
.mv2 strong, .mv2 b { font-weight:500; } .mv2 .sub { color:var(--muted); font-size:13px; } .mv2 .small { font-size:11.5px; } .mv2 .muted { color:var(--muted); }
.mv2 .lbl { font-size:10.5px; font-weight:400; text-transform:uppercase; letter-spacing:.12em; color:var(--muted); margin-bottom:5px; display:block; }
.mv2 .heading, .mv2 .toolbar, .mv2 .section-head { display:flex; align-items:center; gap:10px; justify-content:space-between; flex-wrap:wrap; }
.mv2 .heading { align-items:flex-start; margin-bottom:20px; } .mv2 .toolbar { justify-content:flex-end; }
.mv2 .btn { border:1px solid transparent; border-radius:6px; padding:8px 13px; font-size:12px; font-family:inherit; font-weight:500; cursor:pointer; line-height:1.45; display:inline-block; }
.mv2 .btn-primary { background:var(--text); color:#fff; } .mv2 .btn-ghost { background:var(--surface); color:var(--text); border-color:var(--border); }
.mv2 .btn-danger { background:var(--danger); color:#fff; } .mv2 .btn:disabled { opacity:.5; cursor:not-allowed; }
.mv2 .link { color:var(--text); text-decoration:underline; text-underline-offset:3px; background:none; border:0; font:inherit; cursor:pointer; text-align:left; padding:0; }
.mv2 .revoke { color:var(--danger); font-size:12px; background:none; border:0; cursor:pointer; font-family:inherit; padding:6px 0; }
.mv2 button:focus-visible, .mv2 a:focus-visible, .mv2 input:focus-visible, .mv2 select:focus-visible, .mv2 textarea:focus-visible, .mv2 summary:focus-visible { outline:2px solid var(--text); outline-offset:3px; }
.mv2 .empty { padding:40px; text-align:center; color:var(--muted); background:var(--surface); border:1px solid var(--border); border-radius:8px; }
.mv2 .attention { border:1px solid #E8D8AF; background:#FFFCF4; border-radius:8px; padding:13px 16px; margin-bottom:18px; }
.mv2 .attention .section-head { margin-bottom:8px; }
.mv2 .attention-list { display:grid; grid-template-columns:repeat(2,minmax(0,1fr)); gap:0 24px; }
.mv2 .attention-item { padding:8px 0; border-top:1px solid #EEE4CF; display:flex; gap:9px; align-items:baseline; }
.mv2 .attention-item button { margin-left:auto; flex:none; }
.mv2 .fleet-meta { display:flex; justify-content:space-between; gap:12px; flex-wrap:wrap; margin:12px 0 9px; font-size:11.5px; color:var(--muted); }
.mv2 table { width:100%; border-collapse:separate; border-spacing:0; table-layout:fixed; background:var(--surface); border:1px solid var(--border); border-radius:8px; overflow:hidden; }
.mv2 th { font-size:10.5px; font-weight:400; text-transform:uppercase; letter-spacing:.1em; color:var(--muted); text-align:left; padding:10px 13px; border-bottom:1px solid var(--border); }
.mv2 td { padding:13px; font-size:12px; border-bottom:1px solid var(--border); vertical-align:top; overflow-wrap:anywhere; }
.mv2 tbody:last-child tr:last-child td { border-bottom:0; }
.mv2 th:nth-child(1) { width:25%; } .mv2 th:nth-child(2) { width:15%; } .mv2 th:nth-child(3) { width:18%; } .mv2 th:nth-child(4) { width:23%; } .mv2 th:nth-child(5) { width:12%; } .mv2 th:nth-child(6) { width:7%; }
.mv2 .machine-row:hover { background:#FAF9F7; } .mv2 .machine-row.expanded { background:#F9F8F6; } .mv2 .machine-row.revoked { opacity:.7; }
.mv2 .machine-name { font-weight:500; font-size:13px; background:none; border:0; cursor:pointer; font-family:inherit; text-align:left; color:inherit; padding:0; }
.mv2 .chevron { display:inline-block; width:15px; color:var(--muted); } .mv2 .host { color:var(--muted); font-size:10.5px; margin:3px 0 6px; }
.mv2 .hardware-summary { font-size:11.5px; } .mv2 .row-note { margin-top:6px; font-size:11px; color:var(--muted); }
.mv2 .dot { display:inline-block; width:7px; height:7px; border-radius:50%; margin-right:5px; vertical-align:1px; background:#C8C4BE; }
.mv2 .on { background:var(--green); } .mv2 .work { background:var(--amber); }
.mv2 .chip { display:inline-block; font-size:10.5px; font-weight:500; padding:2px 8px; border-radius:10px; margin:0 3px 4px 0; letter-spacing:.02em; background:var(--surface-alt); }
.mv2 .cap-verify { background:#E7F0FE; color:#2563EB; } .mv2 .cap-capture { background:#FEF0E0; color:#C2620A; }
.mv2 .cap-traffic, .mv2 .cap-backup { background:#E6F6F4; color:#0D9488; } .mv2 .cap-autocycle { background:#EFEDFE; color:#6356E8; }
.mv2 .cap-preview, .mv2 .cap-render { background:#F1EBFD; color:#7850BB; } .mv2 .cap-purge { background:#FDECEC; color:#B91C1C; }
.mv2 .scope { font-size:11px; color:var(--muted); margin:5px 0; }
.mv2 .good-line { display:flex; justify-content:space-between; gap:8px; font-size:11.5px; } .mv2 .score { font-family:'DM Mono',monospace; font-size:11px; }
.mv2 .rank-note { margin-top:5px; font-size:11px; color:var(--muted); }
.mv2 .ver-stale { color:#90600C; } .mv2 .ver-stale .arrow { color:var(--amber); font-size:16px; }
.mv2 .startup-short { margin-top:7px; font-size:11px; color:var(--muted); }
.mv2 .detail-cell { padding:0; background:#FAF9F7; } .mv2 .detail { padding:20px; } .mv2 .detail > .section-head { margin-bottom:15px; }
.mv2 .detail-grid { display:grid; grid-template-columns:minmax(0,1fr) minmax(0,1fr); gap:14px; }
.mv2 .panel { background:var(--surface); border:1px solid var(--border); border-radius:6px; padding:15px; min-width:0; } .mv2 .panel.full { grid-column:1/-1; }
.mv2 .panel p + p { margin-top:7px; }
.mv2 .facts { display:grid; grid-template-columns:110px minmax(0,1fr); gap:5px 12px; font-size:11.5px; } .mv2 .facts dt { color:var(--muted); } .mv2 .facts dd { overflow-wrap:anywhere; }
.mv2 .disk { margin-top:11px; } .mv2 .disk-label { display:flex; gap:12px; justify-content:space-between; font-size:11.5px; }
.mv2 .bar { height:5px; background:var(--surface-alt); border-radius:4px; overflow:hidden; margin:5px 0 3px; } .mv2 .bar span { display:block; height:100%; background:var(--green); border-radius:4px; } .mv2 .bar.low span { background:var(--amber); }
.mv2 .tools { display:grid; grid-template-columns:repeat(2,minmax(0,1fr)); gap:5px 14px; margin:12px 0; }
.mv2 .tool { display:flex; justify-content:space-between; gap:7px; font-size:11px; border-bottom:1px solid #F1EFEC; padding-bottom:4px; } .mv2 .tool span:last-child { text-align:right; }
.mv2 details { margin-top:12px; } .mv2 summary { cursor:pointer; font-size:11.5px; }
.mv2 .history { padding-left:17px; margin-top:8px; font-size:11px; color:var(--muted); } .mv2 .history li { margin:4px 0; }
.mv2 .suit { padding:9px 0; border-top:1px solid var(--border); } .mv2 .suit-head { display:flex; justify-content:space-between; align-items:baseline; gap:8px; }
.mv2 .why { font-size:11.5px; color:var(--muted); margin:4px 0; padding-left:16px; }
.mv2 .override { padding:7px 9px; margin-top:6px; border-left:2px solid var(--amber); background:#FFFCF4; font-size:11px; }
.mv2 .callout { padding:9px 11px; border:1px solid var(--border); border-radius:6px; background:#F8F7F5; font-size:11.5px; margin-top:10px; }
.mv2 .warn { background:#FFFCF4; border-color:#E8D8AF; } .mv2 .purge-warning { background:#FFF5F5; border-color:#F5C2C2; color:#9B2929; }
.mv2 .metrics { display:grid; grid-template-columns:repeat(2,minmax(0,1fr)); gap:10px; }
.mv2 .metric { padding:10px 12px; border:1px solid var(--border); border-radius:6px; font-size:11.5px; } .mv2 .metric strong { display:block; margin-bottom:4px; }
.mv2 .root-grid { display:grid; grid-template-columns:repeat(2,minmax(0,1fr)); gap:12px; }
.mv2 .root { border:1px solid var(--border); border-radius:6px; padding:12px; min-width:0; } .mv2 .root h3 { margin-bottom:8px; }
.mv2 .binding { border-top:1px solid var(--border); margin-top:10px; padding-top:9px; font-size:11.5px; } .mv2 .root .btn { margin-top:10px; }
.mv2 .places { margin-top:20px; } .mv2 .places-list { display:grid; grid-template-columns:repeat(3,minmax(0,1fr)); gap:9px; margin-top:12px; }
.mv2 .place { border:1px solid var(--border); background:var(--surface); padding:11px 13px; border-radius:6px; font-size:11.5px; min-width:0; overflow-wrap:anywhere; }
.mv2 .footer { font-size:11px; color:var(--muted); padding:18px 0 8px; }
.mv2 .overlay { position:fixed; inset:0; background:rgba(26,26,26,.35); display:flex; align-items:center; justify-content:center; z-index:100; padding:14px; }
.mv2 .dialog { position:relative; background:var(--surface); color:var(--text); border:1px solid var(--border); border-radius:10px; width:510px; max-width:100%; max-height:calc(100dvh - 28px); padding:26px; box-shadow:0 12px 40px rgba(0,0,0,.10); overflow:auto; }
.mv2 .dialog h2 { padding-right:25px; margin-bottom:3px; } .mv2 .dialog .sub { font-size:12px; margin-bottom:17px; }
.mv2 .close { position:absolute; right:15px; top:13px; background:none; border:0; font-size:22px; cursor:pointer; color:var(--muted); width:28px; height:28px; }
.mv2 .field { margin-bottom:15px; }
.mv2 .txt { width:100%; padding:10px 13px; background:var(--surface-alt); border:1px solid var(--border); border-radius:6px; font-size:13px; font-family:inherit; color:var(--text); }
.mv2 .code-input { width:100%; padding:14px; border:1px solid var(--border); background:var(--surface-alt); border-radius:6px; font-family:'DM Mono',monospace; font-size:24px; letter-spacing:.25em; text-align:center; color:var(--text); }
.mv2 .caps { display:grid; grid-template-columns:1fr 1fr; gap:8px; }
.mv2 .cap-opt { display:flex; align-items:flex-start; gap:9px; padding:10px; border:1px solid var(--border); border-radius:6px; cursor:pointer; background:var(--surface); }
.mv2 .cap-opt.sel { border-color:var(--text); background:var(--surface-alt); } .mv2 .cap-opt.danger { border-color:#F5C2C2; } .mv2 .cap-opt.danger.sel { border-color:var(--danger); background:#FFF5F5; }
.mv2 .cap-opt input { margin-top:3px; accent-color:var(--text); } .mv2 .cap-opt .t { font-size:13px; font-weight:500; display:block; } .mv2 .cap-opt.danger .t { color:#B91C1C; }
.mv2 .cap-opt .d { font-size:11px; color:var(--muted); margin-top:1px; display:block; } .mv2 .cap-opt.dis { opacity:.45; cursor:not-allowed; }
.mv2 .dialog-actions { display:flex; justify-content:flex-end; gap:8px; margin-top:18px; flex-wrap:wrap; } .mv2 .dialog-actions .btn { padding:10px 15px; }
.mv2 .hint { font-size:11.5px; color:var(--muted); margin-top:10px; } .mv2 .form-error { color:#856013; font-size:12px; margin-top:12px; }
.mv2 .ack { display:flex; align-items:flex-start; gap:8px; font-size:12px; margin:15px 0; } .mv2 .ack input { margin-top:3px; }
.mv2 .toast { position:fixed; bottom:18px; left:50%; transform:translateX(-50%); max-width:calc(100% - 28px); width:max-content; padding:10px 16px; border-radius:6px; background:var(--text); color:white; font-size:12px; box-shadow:0 6px 20px #0002; z-index:110; }
@media (max-width:1050px) { .mv2 { padding:22px; } .mv2 td, .mv2 th { padding:10px; } .mv2 .toolbar { justify-content:flex-start; } }
@media (max-width:767px) {
  .mv2 { padding:16px; } .mv2 .toolbar { width:100%; } .mv2 .toolbar .btn { flex:1; text-align:center; }
  .mv2 .attention { padding:12px; } .mv2 .attention-list { grid-template-columns:1fr; }
  .mv2 table, .mv2 tbody, .mv2 tr, .mv2 td { display:block; width:100%; }
  .mv2 table { border:0; background:transparent; overflow:visible; } .mv2 thead { display:none; }
  .mv2 tbody { margin-bottom:12px; border:1px solid var(--border); border-radius:8px; overflow:hidden; background:var(--surface); }
  .mv2 .machine-row { display:grid; grid-template-columns:minmax(0,1fr) minmax(0,1fr); }
  .mv2 .machine-row td { border-bottom:0; padding:10px 12px; min-width:0; }
  .mv2 .machine-row td:first-child { grid-column:1/-1; border-bottom:1px solid var(--border); padding:13px 12px; }
  .mv2 .machine-row td::before { content:attr(data-label); display:block; text-transform:uppercase; letter-spacing:.1em; font-size:10px; color:var(--muted); margin-bottom:6px; }
  .mv2 .machine-row td:first-child::before, .mv2 .machine-row td:last-child::before { display:none; }
  .mv2 .machine-row td:last-child { display:flex; justify-content:flex-end; align-items:flex-end; }
  .mv2 .detail { padding:12px; border-top:1px solid var(--border); } .mv2 .detail-grid, .mv2 .root-grid, .mv2 .metrics { grid-template-columns:1fr; }
  .mv2 .panel { padding:12px; } .mv2 .facts { grid-template-columns:90px minmax(0,1fr); } .mv2 .places-list { grid-template-columns:1fr; }
  .mv2 .dialog { padding:21px 17px; } .mv2 .caps { gap:6px; } .mv2 .cap-opt { padding:9px 8px; gap:6px; }
  .mv2 .dialog-actions .btn { flex:1; } .mv2 .tools { grid-template-columns:1fr; } .mv2 .section-head { align-items:flex-start; }
}
`;
